import { useEffect, useState, type ReactElement } from 'react';
import { Alert, Badge, Box, Button, Code, Drawer, Group, Stack, Text, Tooltip } from '@mantine/core';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import MegaGrid, { type MegaGridColumn, type MegaGridFacet } from '@/design/MegaGrid/MegaGrid';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { AuditLogViewModel } from './AuditLogViewModel';
import type { AuditLogEntry, AuditScope } from './AuditLogService';

const ACTION_OPTIONS = [
    { value: 'create', label: 'Create' },
    { value: 'update', label: 'Update' },
    { value: 'delete', label: 'Delete' },
    { value: 'assign', label: 'Assign' },
    { value: 'unassign', label: 'Unassign' },
    { value: 'activate', label: 'Activate' },
    { value: 'deactivate', label: 'Deactivate' },
    { value: 'revoke', label: 'Revoke' },
    { value: 'import', label: 'Import' },
    { value: 'export', label: 'Export' },
];

const RESOURCE_OPTIONS = [
    { value: 'tenant', label: 'Tenant' },
    { value: 'user', label: 'User' },
    { value: 'role', label: 'Role' },
    { value: 'permission', label: 'Permission' },
    { value: 'license', label: 'License' },
    { value: 'organization', label: 'Organization' },
    { value: 'department', label: 'Department' },
    { value: 'team', label: 'Team' },
    { value: 'context-profile', label: 'Context profile' },
];

const ACTION_COLORS: Record<string, string> = {
    create: 'green',
    update: 'blue',
    delete: 'red',
    revoke: 'red',
    deactivate: 'orange',
    activate: 'teal',
    assign: 'grape',
    unassign: 'gray',
    import: 'cyan',
    export: 'indigo',
};

function actionColor(action: string): string {
    return ACTION_COLORS[action] ?? 'gray';
}

function formatTimestamp(iso: string): string {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** A monospace, ellipsized identifier with the full value in a tooltip. */
function Mono({ value, max = 160 }: { value: string; max?: number }): ReactElement {
    return (
        <Tooltip label={value} openDelay={300} withArrow>
            <Text ff="monospace" size="xs" c="dimmed" style={{ maxWidth: max, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {value}
            </Text>
        </Tooltip>
    );
}

function StateBlock({ label, state }: { label: string; state: Record<string, unknown> | null }): ReactElement | null {
    if (!state || Object.keys(state).length === 0) return null;
    return (
        <Box>
            <Text size="xs" fw={600} mb={4}>{label}</Text>
            <Code block>{JSON.stringify(state, null, 2)}</Code>
        </Box>
    );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): ReactElement {
    return (
        <Group gap="xs" wrap="nowrap" align="flex-start">
            <Text size="xs" c="dimmed" style={{ minWidth: 88 }}>{label}</Text>
            <Text size="xs" ff={mono ? 'monospace' : undefined} style={{ wordBreak: 'break-all' }}>{value}</Text>
        </Group>
    );
}

function EntryDetail({ entry, showTenant }: { entry: AuditLogEntry; showTenant: boolean }): ReactElement {
    return (
        <Stack gap="md">
            <Group gap="xs">
                <Badge color={actionColor(entry.action)} variant="light">{entry.action}</Badge>
                <Text size="sm" fw={600}>{entry.resourceType}</Text>
            </Group>
            <Text size="sm">{entry.summary || <Text span c="dimmed">No summary</Text>}</Text>
            <Stack gap={6}>
                <DetailRow label="When" value={formatTimestamp(entry.createdAt)} />
                <DetailRow label="Actor" value={entry.actorId} mono />
                {showTenant && <DetailRow label="Tenant" value={entry.tenantId} mono />}
                <DetailRow label="Resource" value={`${entry.resourceType} · ${entry.resourceId}`} />
                {entry.correlationId && <DetailRow label="Correlation" value={entry.correlationId} mono />}
            </Stack>
            <StateBlock label="Previous state" state={entry.previousState} />
            <StateBlock label="New state" state={entry.newState} />
        </Stack>
    );
}

function buildColumns(showTenant: boolean): MegaGridColumn<AuditLogEntry>[] {
    const cols: MegaGridColumn<AuditLogEntry>[] = [
        {
            key: 'when', header: 'When', width: 180, sortValue: (e) => e.createdAt,
            render: (e) => <Text size="xs" style={{ whiteSpace: 'nowrap' }}>{formatTimestamp(e.createdAt)}</Text>,
        },
        {
            key: 'actor', header: 'Actor', width: 180, sortValue: (e) => e.actorId,
            render: (e) => <Mono value={e.actorId} />,
        },
        {
            key: 'action', header: 'Action', width: 120, sortValue: (e) => e.action,
            render: (e) => <Badge color={actionColor(e.action)} variant="light" size="sm">{e.action}</Badge>,
        },
        {
            key: 'resource', header: 'Resource', width: 200, sortValue: (e) => e.resourceType,
            render: (e) => (
                <Box>
                    <Text size="xs" fw={500}>{e.resourceType}</Text>
                    <Mono value={e.resourceId} max={180} />
                </Box>
            ),
        },
    ];
    if (showTenant) {
        cols.push({
            key: 'tenant', header: 'Tenant', width: 160, sortValue: (e) => e.tenantId,
            render: (e) => <Text size="xs">{e.tenantId}</Text>,
        });
    }
    cols.push({
        key: 'summary', header: 'Summary', sortValue: (e) => e.summary,
        render: (e) => <Text size="sm" lineClamp={2}>{e.summary}</Text>,
    });
    return cols;
}

/**
 * The audit-trail grid (SAAS-29/34) — a MegaGrid over a recent server-fetched
 * window, with search, faceted action/resource filters, sort, CSV export and a
 * detail drawer showing the full state snapshots. Pass `scope="operator"` to add
 * the cross-tenant Tenant column.
 */
export default function AuditLogView({ scope }: { scope: AuditScope }): ReactElement {
    const vm = useViewModel(AuditLogViewModel);
    const [selected, setSelected] = useState<AuditLogEntry | null>(null);
    const showTenant = scope === 'operator';

    useEffect(() => { vm.init(scope); }, [vm, scope]);

    const columns = buildColumns(showTenant);
    const facets: MegaGridFacet<AuditLogEntry>[] = [
        { key: 'action', placeholder: 'Action', allLabel: 'All actions', options: ACTION_OPTIONS, accessor: (e) => e.action, width: 150 },
        { key: 'resourceType', placeholder: 'Resource', allLabel: 'All resources', options: RESOURCE_OPTIONS, accessor: (e) => e.resourceType, width: 170 },
    ];

    return (
        <Stack gap="sm">
            {vm.error && <Alert color="red" icon={<AlertTriangle size={16} />}>{vm.error}</Alert>}

            <MegaGrid
                persistenceKey={`audit-${scope}`}
                columns={columns}
                rows={vm.entries}
                getRowKey={(e) => e.id}
                searchAccessor={(e) => `${e.actorId} ${e.tenantId} ${e.action} ${e.resourceType} ${e.resourceId} ${e.summary}`}
                searchPlaceholder="Search audit log"
                filters={facets}
                onRowClick={(e) => setSelected(e)}
                clientPageSize={25}
                loading={vm.loading}
                emptyMessage="No audit entries yet"
                enableExport
                exportName={`audit-log-${scope}`}
                toolbar={
                    <Group gap="sm" wrap="nowrap">
                        {vm.truncated && <Text size="xs" c="dimmed">Latest {vm.entries.length} of {vm.total}</Text>}
                        <Button variant="default" size="xs" leftSection={<RefreshCw size={14} />} onClick={() => void vm.load()}>Refresh</Button>
                    </Group>
                }
            />

            <Drawer opened={selected !== null} onClose={() => setSelected(null)} position="right" size="md" title="Audit entry">
                {selected && <EntryDetail entry={selected} showTenant={showTenant} />}
            </Drawer>
        </Stack>
    );
}
